import { clsx } from 'clsx';
import { Link, useLocation } from 'react-router-dom';
import ConnectButton from './connect-button';

import { getDefaultMarket } from '@/app/router';
import VestMarketsLogo from '@/assets/branding/vest-markets-logo';
import { MIGRATION_NEW_APP_VEST_CAPITAL_URL } from '@/config/migration';
import { ROUTES } from '@/config/routes';
import Settings from '@/features/account/components/settings';
import { useIsMigrated } from '@/hooks/use-migration';
import useCheckAffiliate from '@/features/referrals/api/affiliates/get-affiliate-status';
import { isAffiliateDashboardTier } from '@/features/referrals/api/affiliates/types';
import useFundedAccount from '@/hooks/use-funded-account';
import useViewport from '@/hooks/use-viewport';
import { cn } from '@/lib/utils';
import { useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AnnouncementToast from '../announcement-toast';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '../ui/navigation-menu';

const NAV_LINK_CLASS =
  'flex items-center justify-center gap-0.5 border-b border-border bg-background py-4 text-center transition-colors lg:border-b-0 lg:px-6 xl:px-8';

const MIGRATED_NAV: readonly {
  to: string;
  label: string;
  primaryOnly?: boolean;
}[] = [
  { to: ROUTES.MIGRATION, label: 'navigation.migration' },
  {
    to: ROUTES.LIQUIDITY_PROVIDING,
    label: 'navigation.liquidityProviding',
    primaryOnly: true,
  },
  { to: ROUTES.REFERRALS, label: 'navigation.referrals', primaryOnly: true },
  { to: ROUTES.POINTS, label: 'navigation.points', primaryOnly: true },
];

export const Header = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const viewport = useViewport();
  const { isFundedOrEval, evalAddress, fundedAddress } = useFundedAccount();
  const { data: affiliateData } = useCheckAffiliate();
  const isMigrated = useIsMigrated();

  const [isOpen, setIsOpen] = useState(false);

  useLayoutEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  // const onPartialFill = () => {
  //   const { address, getAccurateTime } = useAccountStore.getState();
  //   if (!address) return;
  //   const { orders, newOrderIds } = useOrdersStore
  //     .getState()
  //     .getOrders(address);
  //   if (newOrderIds.size === 0) return;
  //   const parentOrderId = Array.from(newOrderIds)[0];
  //   const order = orders[parentOrderId] as NewOrder;
  //   const {
  //     marketSpec: { sizeDecimals, priceDecimals },
  //   } = useMarketStore(order.symbol).getState();
  //   orderMsgHandler({
  //     channel: 'account_private',
  //     data: {
  //       args: {
  //         symbol: order.symbol,
  //         isBuy: order.isBuy,
  //         orderType: order.orderType,
  //         limitPrice: bigIntToDecimalStr(order.limitPrice, priceDecimals),
  //         reduceOnly: order.reduceOnly,
  //         initMarginRatio: bigIntToDecimalStr(order.initMarginRatio, 4n),
  //         lastFilledSize: bigIntToDecimalStr(order.size / 2n, sizeDecimals),
  //         lastFilledPrice: bigIntToDecimalStr(order.limitPrice, priceDecimals),
  //         markPrice: bigIntToDecimalStr(order.limitPrice, priceDecimals),
  //         lastFilledTime: getAccurateTime() + 1,
  //         avgFilledPrice: bigIntToDecimalStr(order.size / 2n, sizeDecimals),
  //         postTime: order.postTime,
  //         fees: '0',
  //         status: OrderStatus.PARTIALLY_FILLED,
  //         cumFunding: '0',
  //         nonce: 0,
  //         parentId: parentOrderId,
  //       },
  //       event: 'ORDER',
  //     } as PrivateData,
  //   });
  // };

  // const { mutateAsync } = useCreateFundedWithdraw();

  return (
    <header className="relative z-50 flex h-16 shrink-0 grid-cols-5 border-b border-border bg-background sm:h-20">
      <div className="col-span-4 grid flex-1 grid-cols-[1fr_max-content_max-content] font-mono">
        <div className="flex gap-2 px-6 sm:pl-7 sm:pr-4">
          <button
            onClick={() => setIsOpen((prev) => !prev)}
            className="relative z-40 mr-4 flex w-7 shrink-0 flex-col justify-center gap-1 self-center lg:hidden"
          >
            <div
              className={cn(
                'h-0.5 w-full bg-foreground',
                isOpen ? 'opacity-0' : 'opacity-100',
              )}
            />
            <div
              className={cn(
                'h-0.5 w-full origin-center bg-foreground',
                isOpen ? '-rotate-45 transition-transform' : 'rotate-0',
              )}
            />
            <div
              className={cn(
                'absolute h-0.5 w-full origin-center bg-foreground transition-transform',
                isOpen ? 'rotate-45 opacity-100' : 'rotate-0 opacity-0',
              )}
            />
            <div
              className={cn(
                'h-0.5 w-full bg-foreground',
                isOpen ? 'opacity-0' : 'opacity-100',
              )}
            />
          </button>
          <Link
            to={isMigrated ? ROUTES.MIGRATION : `/trade/${getDefaultMarket()}`}
            className="relative z-40 max-w-24 gap-2 sm:max-w-32 lg:mr-6 xl:mr-12"
          >
            <VestMarketsLogo />
          </Link>
          <div
            className={cn(
              'absolute inset-x-0 top-full z-40 transition-opacity lg:static lg:top-0 lg:z-10',
              isOpen
                ? 'opacity-100'
                : 'pointer-events-none opacity-0 lg:pointer-events-auto lg:opacity-100',
            )}
          >
            <div className="flex flex-col justify-end border-t border-border lg:static lg:grid lg:h-full lg:border-t-0">
              <nav className="flex w-full flex-1 flex-col bg-background lg:w-auto lg:flex-none lg:flex-row xl:gap-2">
                {isMigrated ? (
                  MIGRATED_NAV.filter(
                    ({ primaryOnly }) => !primaryOnly || !isFundedOrEval,
                  ).map(({ to, label }) => (
                    <Link
                      key={to}
                      to={to}
                      className={clsx(NAV_LINK_CLASS, {
                        'text-foreground': pathname === to,
                        'text-vestgrey-100 hover:text-foreground':
                          pathname !== to,
                      })}
                    >
                      {t(label)}
                    </Link>
                  ))
                ) : (
                  <>
                    <Link
                      to={`/trade/${getDefaultMarket()}`}
                      className={clsx(
                        'flex items-center justify-center gap-0.5 border-b border-border bg-background py-4 text-center transition-colors lg:border-b-0 lg:px-6 xl:px-8',
                        {
                          'text-foreground': pathname.startsWith(ROUTES.TRADE),
                          'text-vestgrey-100 hover:text-foreground':
                            !pathname.startsWith(ROUTES.TRADE),
                        },
                      )}
                    >
                      {t('navigation.trade')}
                    </Link>
                    <Link
                      to={ROUTES.MARKETS}
                      className={clsx(
                        'flex items-center justify-center gap-0.5 border-b border-border bg-background py-4 text-center transition-colors lg:border-b-0 lg:px-6 xl:px-8',
                        {
                          'text-foreground': pathname === ROUTES.MARKETS,
                          'text-vestgrey-100 hover:text-foreground':
                            pathname !== ROUTES.MARKETS,
                        },
                      )}
                    >
                      {t('navigation.markets')}
                    </Link>
                    <Link
                      to={ROUTES.PORTFOLIO}
                      className={clsx(
                        'flex items-center justify-center gap-0.5 border-b border-border bg-background py-4 text-center transition-colors lg:border-b-0 lg:px-6 xl:px-8',
                        {
                          'text-foreground': pathname === ROUTES.PORTFOLIO,
                          'text-vestgrey-100 hover:text-foreground':
                            pathname !== ROUTES.PORTFOLIO,
                        },
                      )}
                    >
                      {t('navigation.portfolio')}
                    </Link>
                    {evalAddress || fundedAddress ? (
                      <Link
                        to={ROUTES.ACCOUNTS}
                        className={clsx(NAV_LINK_CLASS, {
                          'text-foreground':
                            pathname === ROUTES.VEST_CAPITAL ||
                            pathname === ROUTES.ACCOUNTS,
                          'text-vestgrey-100 hover:text-foreground':
                            pathname !== ROUTES.VEST_CAPITAL &&
                            pathname !== ROUTES.ACCOUNTS,
                        })}
                      >
                        {t('navigation.vestCapital')}
                      </Link>
                    ) : (
                      <a
                        href={MIGRATION_NEW_APP_VEST_CAPITAL_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={clsx(
                          NAV_LINK_CLASS,
                          'text-vestgrey-100 hover:text-foreground',
                        )}
                        onClick={() => setIsOpen(false)}
                      >
                        {t('navigation.getFunded')}
                      </a>
                    )}
                    {isAffiliateDashboardTier(affiliateData?.profile?.tier) && (
                      <Link
                        to={ROUTES.AFFILIATES}
                        className={clsx(
                          'flex items-center justify-center gap-0.5 border-b border-border bg-background py-4 text-center transition-colors lg:border-b-0 lg:px-6 xl:px-8',
                          {
                            'text-foreground':
                              pathname === ROUTES.AFFILIATES && !isFundedOrEval,
                            'text-vestgrey-100 hover:text-foreground':
                              pathname !== ROUTES.AFFILIATES || isFundedOrEval,
                          },
                        )}
                      >
                        Affiliates
                      </Link>
                    )}
                    {!isFundedOrEval &&
                      (viewport === 'lg' ? (
                        <NavigationMenu>
                          <NavigationMenuList>
                            <NavigationMenuItem>
                              <NavigationMenuTrigger
                                onPointerMove={(e) => e.preventDefault()}
                                onPointerLeave={(e) => e.preventDefault()}
                                className="flex items-center justify-center gap-2 border-b border-border bg-background py-4 text-center text-base font-normal text-vestgrey-100 transition-colors lg:border-b-0 lg:px-6 xl:px-8"
                              >
                                {t('navigation.more')}
                              </NavigationMenuTrigger>
                              <NavigationMenuContent className="border border-border bg-background">
                                <div className="grid w-[600px] grid-cols-3">
                                  <NavigationMenuLink asChild>
                                    <Link
                                      to={ROUTES.LIQUIDITY_PROVIDING}
                                      className="space-y-1 border-r px-6 py-5 font-sans hover:bg-vestgrey-800"
                                    >
                                      <p className="text-lg">
                                        {t('liquidityProviding.title')}
                                      </p>
                                      <p className="line-clamp-2 text-vestgrey-100">
                                        {t('liquidityProviding.description')}
                                      </p>
                                    </Link>
                                  </NavigationMenuLink>
                                  <NavigationMenuLink asChild>
                                    <Link
                                      to={ROUTES.REFERRALS}
                                      className="space-y-1 border-r px-6 py-5 font-sans hover:bg-vestgrey-800"
                                    >
                                      <p className="text-lg">
                                        {t('referrals.title')}
                                      </p>
                                      <p className="line-clamp-2 text-vestgrey-100">
                                        {t('referrals.description')}
                                      </p>
                                    </Link>
                                  </NavigationMenuLink>
                                  <NavigationMenuLink asChild>
                                    <Link
                                      to={ROUTES.POINTS}
                                      className="space-y-1 px-6 py-5 font-sans hover:bg-vestgrey-800"
                                    >
                                      <p className="text-lg">
                                        {t('points.title')}
                                      </p>
                                      <p className="line-clamp-2 text-vestgrey-100">
                                        {t('points.description')}
                                      </p>
                                    </Link>
                                  </NavigationMenuLink>
                                </div>
                              </NavigationMenuContent>
                            </NavigationMenuItem>
                          </NavigationMenuList>
                        </NavigationMenu>
                      ) : (
                        <Accordion collapsible type="single">
                          <AccordionItem value="more" className="border-b-0">
                            <AccordionTrigger className="justify-center gap-2 border-b text-base text-vestgrey-100">
                              {t('navigation.more')}
                            </AccordionTrigger>
                            <AccordionContent>
                              <Link
                                to={ROUTES.LIQUIDITY_PROVIDING}
                                className={clsx(
                                  'flex items-center justify-center gap-0.5 border-b border-border bg-background py-4 text-center transition-colors lg:border-b-0 lg:px-6 xl:px-8',
                                  {
                                    'text-foreground':
                                      pathname === ROUTES.LIQUIDITY_PROVIDING,
                                    'text-vestgrey-100 hover:text-foreground':
                                      pathname !== ROUTES.LIQUIDITY_PROVIDING,
                                  },
                                )}
                              >
                                {t('navigation.liquidityProviding')}
                              </Link>
                              <Link
                                to={ROUTES.REFERRALS}
                                className={clsx(
                                  'flex items-center justify-center gap-0.5 border-b border-border bg-background py-4 text-center transition-colors lg:border-b-0 lg:px-6 xl:px-8',
                                  {
                                    'text-foreground':
                                      pathname === ROUTES.REFERRALS,
                                    'text-vestgrey-100 hover:text-foreground':
                                      pathname !== ROUTES.REFERRALS,
                                  },
                                )}
                              >
                                {t('navigation.referrals')}
                              </Link>
                              <Link
                                to={ROUTES.POINTS}
                                className={clsx(
                                  'flex items-center justify-center gap-0.5 border-b border-border bg-background py-4 text-center transition-colors lg:border-b-0 lg:px-6 xl:px-8',
                                  {
                                    'text-foreground':
                                      pathname === ROUTES.POINTS,
                                    'text-vestgrey-100 hover:text-foreground':
                                      pathname !== ROUTES.POINTS,
                                  },
                                )}
                              >
                                {t('navigation.points')}
                              </Link>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      ))}
                  </>
                )}
              </nav>
            </div>
          </div>
          <div
            className={cn(
              'fixed inset-0 z-30 bg-background transition-opacity lg:hidden',
              isOpen
                ? 'opacity-100'
                : 'pointer-events-none opacity-0 lg:pointer-events-auto lg:opacity-100',
            )}
          />
        </div>
      </div>
      <div className="flex items-center">
        <AnnouncementToast />
        {!isMigrated && <Settings />}
        <ConnectButton />
      </div>
    </header>
  );
};
